import firebase from "firebase/app";
import "firebase/firestore";
import moment from "moment";

import { normalizarUsuario } from "./usuarios";
import { promisePool } from "./recnetApi";
import {
  consultarSuscripcionPuntual,
  resolverEstadoMembresia,
  parseToSeconds,
} from "./suscripciones";

/**
 * Programa de referidos: quiénes se registraron con tu código, cuántos pagan y
 * cuánto cobras este mes.
 *
 * La comisión se realiza cuando el referido *renueva*, así que cuenta en el mes
 * de su fecha de renovación (`current_period_end` de Stripe): el que renueva en
 * agosto suma para agosto, no para este mes.
 *
 * Se consulta la suscripción **por referido** (sólo los tuyos), no el índice
 * masivo de todo el padrón: en el portal no se vuelca la base completa de
 * clientes al navegador.
 */

/** Pesos por referido activo que renueva en el mes. */
export const COMISION_POR_REFERIDO = Number(process.env.VUE_APP_COMISION_REFERIDO) || 50;

/**
 * Referidos de un código, cada uno con su estado de membresía y fecha de
 * renovación. Las consultas a Stripe van con concurrencia limitada.
 */
export async function cargarReferidos(referralCode) {
  const codigo = String(referralCode || "").trim();
  if (!codigo) return [];

  // Los códigos se generan en mayúsculas, pero algunas vistas los muestran (y a
  // veces los guardan) en minúsculas. Se consulta en ambos casos y se juntan
  // sin duplicar, para no perder referidos por diferencia de mayúsculas.
  const variantes = Array.from(new Set([codigo, codigo.toUpperCase(), codigo.toLowerCase()]));
  const db = firebase.firestore();
  const snaps = await Promise.all(
    variantes.map((v) => db.collection("app_usuarios").where("referred_by", "==", v).get())
  );

  const porId = new Map();
  for (const snap of snaps) {
    for (const d of snap.docs) {
      if (!porId.has(d.id)) porId.set(d.id, normalizarUsuario({ id: d.id, ...d.data() }));
    }
  }
  const usuarios = Array.from(porId.values());

  return promisePool(
    usuarios,
    async (u) => {
      let membresia = resolverEstadoMembresia(null);
      try {
        const sub = await consultarSuscripcionPuntual({ email: u.email, stripeId: u.stripe_id });
        if (sub) {
          membresia = resolverEstadoMembresia({
            active: String(sub.status || "").toLowerCase() === "active",
            status: String(sub.status || "").toLowerCase(),
            current_period_end: parseToSeconds(sub.current_period_end),
            cancel_at_period_end: !!sub.cancel_at_period_end,
          });
        }
      } catch (err) {
        // Un referido cuya suscripción no se pudo consultar se cuenta como sin
        // membresía, no rompe la carga del resto.
        console.warn("[referidos] no se pudo consultar la membresía de", u.email, err);
      }

      const renuevaEn = membresia.vigenciaHasta && membresia.vigenciaHasta.isValid()
        ? membresia.vigenciaHasta
        : null;

      return {
        id: u.id,
        name: u.name || "Sin nombre",
        email: u.email,
        phone: u.phone,
        referral_code: u.referral_code || "",
        creadoEnFormatted: u.creadoEnFormatted,
        activo: membresia.activo,
        renuevaEn,
        renovacionTexto: renuevaEn ? renuevaEn.format("DD/MM/YYYY") : "—",
        cuentaEsteMes: membresia.activo && esEsteMes(renuevaEn),
      };
    },
    6
  );
}

/** True si la fecha cae dentro del mes calendario actual. */
function esEsteMes(fecha) {
  return !!fecha && fecha.isSame(moment(), "month") && fecha.isSame(moment(), "year");
}

/** Números para la cabecera del modal. */
export function resumenReferidos(lista) {
  const total = lista.length;
  const conMembresia = lista.filter((r) => r.activo).length;
  const activosEsteMes = lista.filter((r) => r.cuentaEsteMes).length;
  const activosProximoMes = lista.filter(
    (r) => r.activo && esProximoMes(r.renuevaEn)
  ).length;

  return {
    total,
    conMembresia,
    sinMembresia: total - conMembresia,
    activosEsteMes,
    dineroEsteMes: activosEsteMes * COMISION_POR_REFERIDO,
    dineroProximoMes: activosProximoMes * COMISION_POR_REFERIDO,
  };
}

function esProximoMes(fecha) {
  if (!fecha) return false;
  const proximo = moment().add(1, "month");
  return fecha.isSame(proximo, "month") && fecha.isSame(proximo, "year");
}
